import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileImport, faSpinner, faCheckCircle, faExclamationTriangle,
  faTimes, faUpload
} from '@fortawesome/free-solid-svg-icons';
import webTargetService from '../../services/webTargetService';
import webTestReportService from '../../services/webTestReportService';

const IMPORT_TYPES = [
  { value: 'har', label: 'Browser HAR', description: 'Import captured traffic from a browser DevTools HAR export (.har). Materializes requests + can mint an auth profile from the session cookies.', accept: '.har,.json' },
  { value: 'burp_xml', label: 'Burp Suite XML', description: 'Import from Burp Suite proxy history or site map export (.xml)', accept: '.xml' },
  { value: 'openapi', label: 'OpenAPI / Swagger', description: 'Import from OpenAPI 3.0 or Swagger 2.0 specification (.json, .yaml)', accept: '.json,.yaml,.yml' },
  { value: 'postman', label: 'Postman Collection', description: 'Import from Postman Collection v2.1 export (.json)', accept: '.json' }
];

const ROLE_OPTIONS = ['admin', 'superadmin', 'manager', 'user', 'read_only', 'guest'];

const STATIC_EXTS = ['js', 'jsx', 'jsdbx', 'assetx', 'css', 'woff', 'woff2', 'ttf', 'eot', 'otf', 'ico', 'svg', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'mp4', 'webm', 'mp3', 'map'];
const isStaticEntry = (entry) => {
  try {
    const u = new URL(entry.request.url);
    const ext = (u.pathname.split('.').pop() || '').toLowerCase();
    if (STATIC_EXTS.includes(ext)) return true;
    const mime = (entry.response?.content?.mimeType || '').toLowerCase();
    return mime.startsWith('image/') || mime.startsWith('font/') || mime.startsWith('video/') || mime.startsWith('audio/') || mime === 'text/css' || mime.includes('javascript');
  } catch { return true; }
};

/**
 * Repair a truncated HAR JSON string.  Chrome sometimes cuts off large HAR
 * exports mid-write, leaving an unterminated string/object.  We scan the
 * `"entries":[…]` array tracking brace depth so we can chop at the last
 * complete entry object and close the JSON structure properly.
 */
const repairTruncatedHar = (text) => {
  const marker = '"entries"';
  const entriesIdx = text.indexOf(marker);
  if (entriesIdx === -1) return null;
  const bracketIdx = text.indexOf('[', entriesIdx + marker.length);
  if (bracketIdx === -1) return null;

  let depth = 0, lastComplete = -1;
  let inStr = false, esc = false;
  for (let i = bracketIdx + 1; i < text.length; i++) {
    const c = text[i];
    if (esc)           { esc = false; continue; }
    if (c === '\\' && inStr) { esc = true; continue; }
    if (c === '"')     { inStr = !inStr; continue; }
    if (inStr) continue;
    if (c === '{') depth++;
    else if (c === '}') { depth--; if (depth === 0) lastComplete = i; }
  }
  if (lastComplete === -1) return null;
  return text.substring(0, lastComplete + 1) + ']}}';
};

const ImportDialog = ({ webTarget, isDarkMode, onClose, onImported }) => {
  const [importType, setImportType] = useState('har');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // HAR-specific state
  const [harData, setHarData] = useState(null);
  const [harHosts, setHarHosts] = useState([]);           // [{host, count}]
  const [selectedHosts, setSelectedHosts] = useState(new Set());
  const [createProfile, setCreateProfile] = useState(true);
  const [profileRole, setProfileRole] = useState('admin');
  const [skipStatic, setSkipStatic] = useState(true);
  const [dedupe, setDedupe] = useState(true);

  let targetHost = '';
  try { targetHost = new URL(webTarget.baseUrl).hostname; } catch { /* bare baseUrl */ }

  const computeHosts = (har, skip) => {
    const counts = new Map();
    for (const e of (har?.log?.entries || [])) {
      if (skip && isStaticEntry(e)) continue;
      try { const h = new URL(e.request.url).hostname; counts.set(h, (counts.get(h) || 0) + 1); } catch { /* skip */ }
    }
    return [...counts.entries()].map(([host, count]) => ({ host, count })).sort((a, b) => b.count - a.count);
  };

  const [repairWarning, setRepairWarning] = useState(null);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setResult(null); setError(null); setRepairWarning(null);
    if (importType === 'har') {
      try {
        const text = await f.text();
        let har, repaired = false;
        try {
          har = JSON.parse(text);
        } catch (parseErr) {
          // Attempt to repair truncated HAR (Chrome cuts off large exports)
          const fixed = repairTruncatedHar(text);
          if (fixed) {
            try {
              har = JSON.parse(fixed);
              repaired = true;
            } catch { throw parseErr; }
          } else {
            throw parseErr;
          }
        }
        if (!har?.log?.entries) throw new Error('Not a valid HAR (missing log.entries)');
        if (repaired) {
          // Count how many entries survived vs approximate total from file size
          const survived = har.log.entries.length;
          setRepairWarning(`HAR file was truncated — repaired automatically. ${survived} complete entries recovered.`);
        }
        setHarData(har);
        const hosts = computeHosts(har, skipStatic);
        setHarHosts(hosts);
        // Pre-select the app's main host only.
        setSelectedHosts(new Set(hosts.some(h => h.host === targetHost) ? [targetHost] : []));
      } catch (err) {
        setError('Could not parse HAR file: ' + err.message);
        setHarData(null); setHarHosts([]); setSelectedHosts(new Set());
      }
    }
  };

  // Build a SLIM HAR to upload: only selected hosts, non-static, with response
  // bodies capped. This turns a 30 MB HAR into a small payload (the bulk of a
  // HAR's size is static assets + large response bodies we don't need). The
  // backend still dedupes and mints the profile from the cookie jar.
  const RESP_BODY_CAP = 2 * 1024 * 1024; // keep response bodies up to 2 MB (full baselines for HAR-as-baseline)
  const buildSlimHar = () => {
    const entries = [];
    for (const e of (harData?.log?.entries || [])) {
      let host; try { host = new URL(e.request.url).hostname; } catch { continue; }
      if (selectedHosts.size && !selectedHosts.has(host)) continue;
      if (skipStatic && isStaticEntry(e)) continue;
      const rc = (e.response && e.response.content) || {};
      const text = (rc.text && rc.text.length <= RESP_BODY_CAP) ? rc.text : '';
      entries.push({
        request: {
          method: e.request.method, url: e.request.url,
          headers: e.request.headers || [], queryString: e.request.queryString || [],
          cookies: e.request.cookies || [],
          postData: e.request.postData ? { text: e.request.postData.text, mimeType: e.request.postData.mimeType } : undefined
        },
        response: {
          status: e.response?.status, statusText: e.response?.statusText,
          headers: e.response?.headers || [],
          content: { mimeType: rc.mimeType, size: rc.size, encoding: rc.encoding, text }
        },
        timings: e.timings, time: e.time
      });
    }
    return { log: { version: harData?.log?.version || '1.2', creator: harData?.log?.creator, entries } };
  };

  const toggleSkipStatic = (val) => {
    setSkipStatic(val);
    if (harData) {
      const hosts = computeHosts(harData, val);
      setHarHosts(hosts);
      setSelectedHosts(prev => new Set([...prev].filter(h => hosts.some(x => x.host === h))));
    }
  };

  const toggleHost = (host) => {
    setSelectedHosts(prev => { const n = new Set(prev); n.has(host) ? n.delete(host) : n.add(host); return n; });
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true); setError(null); setResult(null);
    try {
      let payload;
      if (importType === 'har') {
        if (!harData) throw new Error('HAR not parsed');
        // Upload only the slimmed set (selected hosts, non-static, capped bodies).
        // skipStatic already applied here; selectedHosts passed so the backend
        // keeps exactly those hosts. Backend still dedupes + mints the profile.
        const slim = buildSlimHar();
        const res = await webTestReportService.importHar(webTarget._id, slim, {
          createProfile, profileRole, skipStatic: false, dedupe, selectedHosts: [...selectedHosts]
        });
        payload = { har: true, ...res };
      } else {
        const text = await file.text();
        payload = await webTargetService.importData(webTarget._id, { type: importType, data: text, filename: file.name });
      }
      setResult(payload);
      onImported?.(payload);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = IMPORT_TYPES.find(t => t.value === importType);
  const isHar = importType === 'har';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className={`w-[560px] max-h-[88vh] overflow-y-auto rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faFileImport} className="text-teal-400" />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Import Data</span>
          </div>
          <button onClick={onClose} className={`p-1 rounded ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="space-y-2">
            {IMPORT_TYPES.map(type => (
              <label key={type.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  importType === type.value
                    ? (isDarkMode ? 'border-teal-600 bg-teal-900/10' : 'border-teal-400 bg-teal-50')
                    : (isDarkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300')
                }`}>
                <input type="radio" name="importType" value={type.value} checked={importType === type.value}
                  onChange={() => { setImportType(type.value); setFile(null); setResult(null); setError(null); setHarData(null); setHarHosts([]); }}
                  className="mt-0.5" />
                <div>
                  <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{type.label}</div>
                  <div className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{type.description}</div>
                </div>
              </label>
            ))}
          </div>

          <div className={`p-4 rounded-lg border-2 border-dashed text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
            <input type="file" id="import-file" accept={selectedType?.accept} onChange={handleFileChange} className="hidden" />
            <label htmlFor="import-file" className="cursor-pointer">
              <FontAwesomeIcon icon={faUpload} className={`text-2xl mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {file ? <span className="text-teal-400">{file.name} ({(file.size / 1024).toFixed(1)} KB)</span> : 'Click to select a file or drag and drop'}
              </div>
              <div className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>Accepts: {selectedType?.accept}</div>
            </label>
          </div>

          {/* HAR options */}
          {isHar && harData && (
            <div className={`space-y-3 p-3 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={skipStatic} onChange={e => toggleSkipStatic(e.target.checked)} />
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Skip static assets (images, CSS, JS, fonts, media)</span>
              </label>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={dedupe} onChange={e => setDedupe(e.target.checked)} />
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Deduplicate repeated requests (same method + URL + body) — useful if you revisited pages</span>
              </label>

              <div>
                <div className={`text-[11px] font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Hosts to import ({selectedHosts.size} selected) — the app host is pre-selected
                </div>
                <div className={`max-h-40 overflow-y-auto rounded border divide-y ${isDarkMode ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-100'}`}>
                  {harHosts.map(({ host, count }) => (
                    <label key={host} className={`flex items-center justify-between gap-2 px-2 py-1 text-xs cursor-pointer ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <input type="checkbox" checked={selectedHosts.has(host)} onChange={() => toggleHost(host)} />
                        <span className={`truncate ${host === targetHost ? 'font-semibold text-teal-400' : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>{host}</span>
                        {host === targetHost && <span className="text-[9px] px-1 rounded bg-teal-600 text-white">app host</span>}
                      </span>
                      <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>{count}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => setSelectedHosts(new Set(harHosts.map(h => h.host)))} className="text-[10px] text-teal-500 hover:underline">Select all</button>
                  <button onClick={() => setSelectedHosts(new Set(harHosts.some(h => h.host === targetHost) ? [targetHost] : []))} className="text-[10px] text-gray-400 hover:underline">App host only</button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={createProfile} onChange={e => setCreateProfile(e.target.checked)} />
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Create auth profile from the session cookies &amp; attribute traffic to it</span>
              </label>
              {createProfile && (
                <div className="flex items-center gap-2 text-xs pl-6">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Role:</span>
                  <select value={profileRole} onChange={e => setProfileRole(e.target.value)}
                    className={`rounded border px-2 py-1 text-xs ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}>
                    {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Repair warning */}
          {repairWarning && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400 mt-0.5" />
              <div className={`text-xs ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>{repairWarning}</div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${isDarkMode ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-400 mt-0.5" />
              <div className="text-xs">
                <div className={`font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>Import successful</div>
                <div className={isDarkMode ? 'text-green-400/70' : 'text-green-600'}>
                  {result.har ? (
                    <>
                      {result.imported} request(s) imported{result.duplicatesRemoved ? `, ${result.duplicatesRemoved} duplicate(s) merged` : ''}{result.skipped ? `, ${result.skipped} skipped` : ''}.
                      {result.profile
                        ? <> Profile {result.profile.updated ? 'updated' : 'created'}: <b>{result.profile.name}</b> ({result.profile.role}, {result.profile.cookieCount} cookies){result.profile.identity ? ` — ${result.profile.identity}` : ''}.</>
                        : ' No profile created.'}
                      {result.message ? ` ${result.message}` : ''}
                    </>
                  ) : (
                    <>
                      {result.exchangesImported !== undefined && `${result.exchangesImported} exchanges imported. `}
                      {result.siteMapEntries !== undefined && `${result.siteMapEntries} site map entries added.`}
                      {result.endpointsImported !== undefined && `${result.endpointsImported} endpoints imported.`}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${isDarkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-400 mt-0.5" />
              <div className="text-xs">
                <div className={`font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Import failed</div>
                <div className={isDarkMode ? 'text-red-400/70' : 'text-red-600'}>{error}</div>
              </div>
            </div>
          )}
        </div>

        <div className={`flex justify-end gap-2 px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button onClick={onClose}
            className={`px-4 py-1.5 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={handleImport} disabled={loading || !file || (isHar && (!harData || selectedHosts.size === 0))}
              className="px-4 py-1.5 bg-teal-600 text-white rounded text-xs hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5">
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFileImport} />}
              Import
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportDialog;
